import React, { useEffect, useState } from "react";

const TrekBasicInfo = ({
  trekName = "Korigad Fort",
  location = "Nashik, Maharashtra",
  trekDifficulty = "Moderate",
  ageLimit = "16+ Years",
}) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);

        // Using wttr.in API - free, no authentication needed
        const weatherUrl = `https://wttr.in/${location}?format=j1`;

        console.log("Fetching weather from:", weatherUrl);

        const weatherResponse = await fetch(weatherUrl);

        if (!weatherResponse.ok) {
          throw new Error(`API Error: ${weatherResponse.status}`);
        }

        const weatherData = await weatherResponse.json();
        console.log("Full weather response:", weatherData);

        // Extract current weather data
        const currentCondition =
          weatherData?.current_condition?.[0] ||
          weatherData?.weather?.[0]?.hourly?.[0];

        if (!currentCondition) {
          throw new Error("No current weather data found");
        }

        console.log("Current condition:", currentCondition);

        setWeather({
          temp: parseInt(
            currentCondition.temp_C || currentCondition.temperature || 28,
          ),
          description:
            currentCondition.weatherDesc?.[0]?.value ||
            currentCondition.condition ||
            "Partly Cloudy",
          humidity: parseInt(currentCondition.humidity || 65),
          windSpeed: Math.round(
            currentCondition.windspeedKmph || currentCondition.wind_speed || 3,
          ),
        });
        setError(null);
      } catch (err) {
        console.error("Weather fetch error:", err.message);
        setWeather(null);
        setError("Weather details are currently unavailable.");
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [location]);

  return (
    <div className="bg-white shadow-md rounded-lg p-4 sm:p-6 mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">
        Trek Essentials
      </h2>

      {/* Main Cards Grid - 2 columns on mobile, 4 on desktop */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Trek Difficulty */}
        <div className="flex flex-col items-center justify-center p-4 sm:p-5 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">⛰️</div>
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 text-center">
            Difficulty
          </h3>
          <p className="text-base sm:text-lg font-bold text-blue-700">
            {trekDifficulty}
          </p>
        </div>

        {/* Age Limit */}
        <div className="flex flex-col items-center justify-center p-4 sm:p-5 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">👥</div>
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 text-center">
            Age Limit
          </h3>
          <p className="text-base sm:text-lg font-bold text-green-700">
            {ageLimit}
          </p>
        </div>

        {/* Duration */}
        <div className="flex flex-col items-center justify-center p-4 sm:p-5 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border-2 border-orange-200">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">⏱️</div>
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 text-center">
            Duration
          </h3>
          <p className="text-base sm:text-lg font-bold text-orange-700">
            1 Day
          </p>
        </div>

        {/* Weather Condition */}
        <div className="flex flex-col items-center justify-center p-4 sm:p-5 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border-2 border-purple-200">
          <div className="text-3xl sm:text-4xl mb-2 sm:mb-3">☁️</div>
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2 text-center">
            Weather
          </h3>
          {loading ? (
            <p className="text-base sm:text-lg font-bold text-purple-700">
              ...
            </p>
          ) : error ? (
            <p className="text-sm font-semibold text-gray-600 text-center">
              {error}
            </p>
          ) : weather ? (
            <p className="text-base sm:text-lg font-bold text-purple-700 text-center line-clamp-2">
              {weather.description}
            </p>
          ) : (
            <p className="text-base sm:text-lg font-bold text-purple-700">
              N/A
            </p>
          )}
        </div>
      </div>

      {/* Additional Info - 2 columns grid */}
      {weather && weather.humidity && (
        <div className="mt-5 sm:mt-6 p-4 sm:p-5 bg-gray-50 rounded-lg border-2 border-gray-200">
          <h3 className="text-xs sm:text-sm font-bold text-gray-700 mb-3 sm:mb-4 uppercase">
            Details
          </h3>
          <div className="grid grid-cols-2 gap-4 sm:gap-6">
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-600 mb-1 uppercase">
                Humidity
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {weather.humidity}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold text-gray-600 mb-1 uppercase">
                Wind Speed
              </p>
              <p className="text-lg sm:text-2xl font-bold text-gray-800">
                {weather.windSpeed} m/s
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrekBasicInfo;
